import { useState, useEffect } from "react";
import { api, formatApiError, resolveImg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Upload, X, ImagePlus, GripVertical, Star } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  brand: "", model: "", year: 2020, price: 0, mileage: 0, fuel: "Diesel",
  transmission: "Automatique", color: "", doors: 5, equipment: "", description: "",
  images: [], status: "disponible", featured: false,
};

export function VehicleFormDialog({ open, onOpenChange, vehicle, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const reorder = (from, to) =>
    setForm((f) => {
      const imgs = [...f.images];
      const [m] = imgs.splice(from, 1);
      imgs.splice(to, 0, m);
      return { ...f, images: imgs };
    });

  useEffect(() => {
    if (vehicle) {
      setForm({
        ...vehicle,
        equipment: (vehicle.equipment || []).join(", "),
        images: vehicle.images || [],
      });
    } else setForm(EMPTY);
  }, [vehicle, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/vehicles/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      }
      toast.success("Photo(s) ajoutée(s)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'envoi");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (url) => setForm((f) => ({ ...f, images: f.images.filter((x) => x !== url) }));

  const submit = async () => {
    setLoading(true);
    const payload = {
      brand: form.brand, model: form.model, year: Number(form.year), price: Number(form.price),
      mileage: Number(form.mileage), fuel: form.fuel, transmission: form.transmission,
      color: form.color, doors: Number(form.doors),
      equipment: form.equipment.split(",").map((s) => s.trim()).filter(Boolean),
      description: form.description,
      images: form.images,
      status: form.status, featured: !!form.featured,
    };
    try {
      if (vehicle) await api.put(`/vehicles/${vehicle.id}`, payload);
      else await api.post("/vehicles", payload);
      toast.success(vehicle ? "Véhicule mis à jour" : "Véhicule ajouté");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const field = (k, label, type = "text") => (
    <div>
      <Label className="text-neutral-300 mb-1.5 block text-sm">{label}</Label>
      <Input data-testid={`vehicle-field-${k}`} type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} className="bg-[#0a0a0a] border-white/15 text-white focus-visible:ring-[#C5A059]" />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">{vehicle ? "Modifier le véhicule" : "Ajouter un véhicule"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2 py-2">
          {field("brand", "Marque")}
          {field("model", "Modèle")}
          {field("year", "Année", "number")}
          {field("price", "Prix (€)", "number")}
          {field("mileage", "Kilométrage", "number")}
          {field("color", "Couleur")}
          <div>
            <Label className="text-neutral-300 mb-1.5 block text-sm">Énergie</Label>
            <Select value={form.fuel} onValueChange={(v) => set("fuel", v)}>
              <SelectTrigger data-testid="vehicle-field-fuel" className="bg-[#0a0a0a] border-white/15 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>{["Essence", "Diesel", "Hybride", "Électrique"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-neutral-300 mb-1.5 block text-sm">Boîte</Label>
            <Select value={form.transmission} onValueChange={(v) => set("transmission", v)}>
              <SelectTrigger data-testid="vehicle-field-transmission" className="bg-[#0a0a0a] border-white/15 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>{["Automatique", "Manuelle"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {field("doors", "Portes", "number")}
          <div>
            <Label className="text-neutral-300 mb-1.5 block text-sm">Statut</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="vehicle-field-status" className="bg-[#0a0a0a] border-white/15 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>{["disponible", "vendu"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-neutral-300 mb-1.5 block text-sm">Équipements (séparés par des virgules)</Label>
            <Input data-testid="vehicle-field-equipment" value={form.equipment} onChange={(e) => set("equipment", e.target.value)} className="bg-[#0a0a0a] border-white/15 text-white" placeholder="GPS, Caméra, Cuir..." />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-neutral-300 mb-1.5 block text-sm">Photos du véhicule</Label>
            <p className="text-xs text-neutral-500 mb-3">Glissez-déposez pour réordonner. La 1ʳᵉ photo est l'image principale.</p>
            <div className="flex flex-wrap gap-3">
              {form.images.map((url, i) => (
                <div
                  key={url}
                  data-testid={`vehicle-image-${i}`}
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { if (dragIdx !== null && dragIdx !== i) reorder(dragIdx, i); setDragIdx(null); }}
                  className={`relative h-20 w-28 rounded-sm overflow-hidden border cursor-grab active:cursor-grabbing group ${dragIdx === i ? "border-[#C5A059] opacity-60" : "border-white/10"}`}
                >
                  <img src={resolveImg(url)} alt="" className="h-full w-full object-cover pointer-events-none" />
                  {i === 0 && <span className="absolute bottom-1 left-1 flex items-center gap-1 bg-[#C5A059] text-black text-[9px] font-bold px-1.5 py-0.5 rounded-sm"><Star className="h-2.5 w-2.5" /> Principale</span>}
                  <span className="absolute top-1 left-1 text-white/70"><GripVertical className="h-3.5 w-3.5" /></span>
                  <button type="button" data-testid={`remove-image-btn-${i}`} onClick={() => removeImage(url)} className="absolute top-1 right-1 h-6 w-6 flex items-center justify-center rounded-full bg-black/70 text-white hover:bg-red-600 transition-colors">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <label data-testid="upload-image-label" className="h-20 w-28 flex flex-col items-center justify-center gap-1 rounded-sm border border-dashed border-white/20 text-neutral-400 hover:border-[#C5A059] hover:text-[#C5A059] cursor-pointer transition-colors">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                <span className="text-[11px]">{uploading ? "Envoi..." : "Ajouter"}</span>
                <input data-testid="vehicle-image-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
            </div>
            <p className="text-xs text-neutral-500 mt-2">Formats JPG, PNG, WEBP, GIF — 8 Mo max par image.</p>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-neutral-300 mb-1.5 block text-sm">Description</Label>
            <Textarea data-testid="vehicle-field-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="bg-[#0a0a0a] border-white/15 text-white" />
          </div>
          <div className="sm:col-span-2 flex items-center gap-3">
            <Switch data-testid="vehicle-field-featured" checked={form.featured} onCheckedChange={(v) => set("featured", v)} />
            <Label className="text-neutral-300 text-sm">Mettre en avant sur l'accueil</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/20 text-white hover:bg-white/5 rounded-sm">Annuler</Button>
          <Button data-testid="vehicle-save-btn" onClick={submit} disabled={loading || !form.brand || !form.model} className="bg-[#C5A059] text-black hover:bg-[#E6C678] rounded-sm">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
